import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import { ReactComponent as CityIcon } from 'assets/icons/city.svg'
import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow_short_right.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import { navigateToStudentProfile } from '../../utils/priorityCardUi'
import s from './PriorityDrawerShell.module.scss'

const buildDefaultMeta = item => {
	if (!item) return ''
	return [item.universityName, item.countryCode, item.programName]
		.filter(Boolean)
		.join(' · ')
}

const NavButton = ({
	target,
	direction,
	disabled,
	navigationType,
	onClick,
}) => {
	const isPrev = direction === 'prev'
	const label = isPrev ? 'Previous' : 'Next'
	const isEssayNav = navigationType === 'essay'

	const arrow = (
		<span
			className={[s.navIcon, isPrev ? s.navIconLeft : '']
				.filter(Boolean)
				.join(' ')}
		>
			<ArrowRightIcon />
		</span>
	)

	const detailed = navigationType === 'detailed'
	const avatar =
		detailed && target ? (
			<span className={s.navAvatar}>
				<InitialAvatar
					size={28}
					name={target.studentName || 'Student'}
					studentId={target.studentId}
					photoUrl={target.studentAvatarUrl}
					avatarSource={target.studentAvatarUrl ? 'photo' : undefined}
				/>
			</span>
		) : null
	const navTitle = isEssayNav
		? target?.title || 'Essay'
		: detailed
			? target?.studentName
			: null
	const textBlock = (
		<span className={s.navText}>
			<span className={s.navLabel}>{label}</span>
			{navTitle ? <span className={s.navName}>{navTitle}</span> : null}
		</span>
	)

	return (
		<button
			type="button"
			className={s.navButton}
			disabled={disabled}
			onClick={onClick}
		>
			{isPrev ? (
				<>
					{arrow}
					{avatar}
					{textBlock}
				</>
			) : (
				<>
					{textBlock}
					{avatar}
					{arrow}
				</>
			)}
		</button>
	)
}

const PriorityDrawerShell = ({
	item,
	items = [],
	index = 0,
	onClose,
	onNavigate,
	wide = false,
	feedbackOnly = false,
	headerActions = null,
	headerLeadingActions = null,
	navigationType = 'simple',
	showFooter = true,
	footerContent = null,
	meta,
	bodyClassName = '',
	headerContent = null,
	children,
}) => {
	const navigate = useNavigate()
	const [isClosing, setIsClosing] = useState(false)

	if (!item) return null

	const metaText = meta != null ? meta : buildDefaultMeta(item)
	const canPrev = index > 0
	const canNext = index < items.length - 1
	const prevItem = canPrev ? items[index - 1] : null
	const nextItem = canNext ? items[index + 1] : null
	const showNav =
		showFooter && items.length > 0 && typeof onNavigate === 'function'
	const studentName = item.studentName || 'Student'
	const canOpenStudent = Boolean(String(item.studentId || '').trim())

	const openStudentProfile = e => {
		e?.stopPropagation?.()
		navigateToStudentProfile(navigate, item.studentId)
	}

	const requestClose = () => {
		if (isClosing) return
		setIsClosing(true)
		window.setTimeout(() => onClose?.(), 200)
	}

	const wrapClassName = [
		s.wrap,
		wide ? s.expanded : '',
		feedbackOnly ? s.feedbackOnly : '',
		isClosing ? s.closing : '',
	]
		.filter(Boolean)
		.join(' ')
	const scrimClassName = [s.scrim, isClosing ? s.scrimClosing : '']
		.filter(Boolean)
		.join(' ')
	const bodyClasses = [s.body, bodyClassName].filter(Boolean).join(' ')

	const overlay = (
		<div className={s.overlay}>
			<div className={scrimClassName} onClick={requestClose} />
			<aside className={wrapClassName} onClick={e => e.stopPropagation()}>
				<header className={s.header}>
					<div className={s.identity}>
						<span className={s.avatarWrap}>
							<InitialAvatar
								size={44}
								name={studentName}
								studentId={item.studentId}
								photoUrl={item.studentAvatarUrl}
								avatarSource={item.studentAvatarUrl ? 'photo' : undefined}
								onClick={canOpenStudent ? openStudentProfile : undefined}
							/>
						</span>
						<div className={s.identityText}>
							{canOpenStudent ? (
								<button
									type="button"
									className={s.title}
									onClick={openStudentProfile}
								>
									{studentName}
								</button>
							) : (
								<h2 className={s.title}>{studentName}</h2>
							)}
							{metaText ? (
								<p className={s.meta}>
									<CityIcon />
									<span>{metaText}</span>
								</p>
							) : null}
						</div>
					</div>
					<div className={s.actions}>
						{headerLeadingActions}
						{headerActions}
						<button
							type="button"
							className={s.close}
							onClick={requestClose}
							aria-label="Close drawer"
						>
							<span className={s.close__icon}>
								<CrossIcon />
							</span>
						</button>
					</div>
				</header>
				<div className={bodyClasses}>{children}</div>
				{footerContent ? (
					<footer className={s.footer}>{footerContent}</footer>
				) : showNav ? (
					<footer className={s.footer}>
						<NavButton
							target={prevItem}
							direction="prev"
							disabled={!canPrev}
							navigationType={navigationType}
							onClick={() => onNavigate?.(index - 1)}
						/>
						<span className={s.position}>
							{index + 1} / {items.length}
						</span>
						<NavButton
							target={nextItem}
							direction="next"
							disabled={!canNext}
							navigationType={navigationType}
							onClick={() => onNavigate?.(index + 1)}
						/>
					</footer>
				) : null}
			</aside>
		</div>
	)

	return typeof document !== 'undefined'
		? createPortal(overlay, document.body)
		: overlay
}

export default PriorityDrawerShell
